import { Check, Eye } from "lucide-react";
import type { FC } from "react";

import { Surface } from "@/components/surface";
import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge } from "@/components/ui/status-badge";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import { useCaseIde } from "../../context/CaseIdeContext";
import { useWorkflows } from "../context/WorkflowsContext";
import { ORANGE_DARK, STATUS_META } from "../status";
import type { WorkflowDef, WorkflowId, WorkflowView } from "../types";
import { NumberStyleSelect } from "./NumberStyleSelect";
import { PacketContentsSelect } from "./PacketContentsSelect";
import { WorkflowRunButton } from "./WorkflowRunButton";

type Props = {
  def: WorkflowDef;
  view: WorkflowView;
  /** The step to do next: accent outline and the view's one primary button. */
  nextUp?: boolean;
};

const MAX_REASONS = 2;

/** A reason's line on the card: "Journal Standing failed" for a failure, else its first line. */
const reasonLine = (text: string): string => {
  const [line] = text.split("\n");
  return /^(.+?\bfailed)(?::|$)/.exec(line)?.[1] ?? line;
};

/** One reason on one line; the whole of it, the server's wording too, in its tooltip. */
const Reason: FC<{ text: string }> = ({ text }) => (
  <li className="truncate" title={text}>
    {reasonLine(text)}
  </li>
);

/** What's behind the status, two items and a count of the rest. */
const Reasons: FC<{ id: string; reasons: string[] }> = ({ id, reasons }) => {
  if (!reasons.length) return null;
  const rest = reasons.length - MAX_REASONS;
  return (
    <ul
      className="flex flex-col gap-0.5 text-sm text-fg-2"
      data-testid={`workflow-reasons-${id}`}
    >
      {reasons.slice(0, MAX_REASONS).map((r) => (
        <Reason key={r} text={r} />
      ))}
      {rest > 0 ? (
        <li
          className="text-fg-4"
          title={reasons
            .slice(MAX_REASONS)
            .map((r) => r.split("\n")[0])
            .join("\n")}
        >
          +{rest} more
        </li>
      ) : null}
    </ul>
  );
};

/** Steps whose items are the substance of the card: forms, letters, files to sort. */
const ITEM_STEPS: ReadonlySet<WorkflowId> = new Set([
  "organize",
  "letters",
  "forms"
]);

/**
 * Items show for those steps and for anything waiting on an answer or failed.
 * A step held in its run says what holds it in its one line; the reasons and
 * the way out go in that line's tooltip.
 */
const showsItems = (id: WorkflowId, view: WorkflowView) =>
  !(view.live && view.opens === "thread") &&
  (ITEM_STEPS.has(id) ||
    view.live === true ||
    view.status === "needs-you" ||
    view.status === "failed");

const tooltip = (...lines: (string | null | undefined)[]) =>
  lines.filter(Boolean).join("\n") || undefined;

/** Check Case Insights' way to the values it proposed, whatever its status. */
const ViewInsights: FC = () => {
  const { individualId, open } = useCaseIde();
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className="ml-auto shrink-0"
      aria-label="View Insights"
      title="View Insights"
      onClick={() =>
        open({ kind: "key_facts", id: individualId, title: "Case Insights" })
      }
      data-testid="workflow-view-insights"
    >
      <Eye />
    </Button>
  );
};

/**
 * One workflow: its title, the items that matter and one status line. What
 * it does is the title's tooltip; its settings are in the options popover.
 */
export const WorkflowCard: FC<Props> = ({ def, view, nextUp = false }) => {
  const { settings } = useWorkflows();
  const meta = STATUS_META[view.status];
  const items = showsItems(def.id, view) ? view.reasons ?? [] : [];
  const hidden = items.length ? [] : view.reasons ?? [];
  const ranOn = view.model ? `Ran on ${view.model}` : null;
  return (
    <Surface
      className={cn(
        "workflow-card gap-2 p-(--wf-pad)",
        nextUp && "ring-accent-line"
      )}
      data-testid={`workflow-card-${def.id}`}
      data-status={view.status}
      data-next-up={nextUp ? "true" : undefined}
    >
      <div className="flex items-center gap-2.5">
        <IconTile icon={def.icon} />
        <span
          className="truncate text-base font-medium text-fg"
          title={def.description}
        >
          {def.title}
        </span>
        {def.optional ? (
          <Tag
            title="Not part of Run Everything. Run it from here."
            data-testid={`workflow-optional-${def.id}`}
          >
            Optional
          </Tag>
        ) : null}
        {nextUp ? (
          <span className="shrink-0 text-sm text-accent-seed">Next Up</span>
        ) : null}
        {def.id === "insights" ? <ViewInsights /> : null}
      </div>
      {def.id === "packet" && view.status !== "running" ? (
        <PacketContentsSelect />
      ) : null}
      <Reasons id={def.id} reasons={items} />
      {def.kind === "direct" ? (
        <div className="mt-auto flex items-center gap-2">
          <StatusBadge
            tone={view.status === "done" ? "success" : "neutral"}
            dot={view.status !== "done"}
            title={tooltip(view.detail, def.summary(settings))}
            data-testid={`workflow-status-${def.id}`}
          >
            {view.status === "done" ? <Check aria-hidden /> : null}
            {view.status === "done" ? "Set" : "Not Set"}
          </StatusBadge>
          <NumberStyleSelect />
        </div>
      ) : (
        <div className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <StatusBadge
            tone={meta.tone}
            dot={view.status !== "running"}
            className={cn(meta.tone === "orange" && ORANGE_DARK)}
            data-testid={`workflow-status-${def.id}`}
          >
            {view.status === "running" ? <Spinner aria-hidden /> : null}
            {meta.label}
          </StatusBadge>
          <span
            className="min-w-32 flex-1 truncate text-sm text-fg-2"
            title={tooltip(view.detail, ...hidden, ranOn)}
            data-testid={`workflow-detail-${def.id}`}
          >
            {view.detail}
          </span>
          <div className="ml-auto">
            <WorkflowRunButton def={def} view={view} primary={nextUp} />
          </div>
        </div>
      )}
    </Surface>
  );
};
